import React, { forwardRef, useContext, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    useDataModel,
    QueryContext,
    extend,
    string,
    bool,
    params
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
    handleProductClick, hasSameDayFilter, impProductPodData, getPageTypeFromPath, stringifyItemIds,
    stringifyAppliedRefinements, isNIncluded
} from '../util';
import { DefaultGrid } from '../components/DefaultGrid';
import { debounce } from '@thd-olt-functional/utils';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { OrderSample, usePLPSamples } from '@thd-olt-component-react/order-sample';

const DefaultGridComposable = forwardRef(({ itemId, productSSR, searchSSR }, ref) => {
    const { customContext, channel, path, deliveryZip } = useContext(ExperienceContext);
    const {
        variables, varnish, hybridLayoutID, isHybridLayout, isBrandPage, isSearch
    } = customContext || {};
    const pageType = getPageTypeFromPath(path);
    const store = useStore({ varnish, online: false });
    const { isCustomerIdentified = false } = useThdCustomer();
    const { isClientResolved, defaultVariables } = useContext(QueryContext);
    const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });
    const [clientSearchModelDone, setClientSearchModelDone] = useState(false);
    const samplesLoadedForCurrentPage = useRef(false);

    // Create internal ref only if no external ref provided
    const internalRef = useRef(null);
    // Use the external ref if provided, otherwise use our internal ref
    const resultsDimensionsRef = ref || internalRef;

    const loadedStore = useRef(store);
    const localizedAndClientLoaded = useRef(false);

    const searcModelVariables = isSearch
      ? {
          ...variables,
          isBrandPricingPolicyCompliant: defaultVariables.current?.isBrandPricingPolicyCompliant,
        }
      : variables;

    const {
        additionalSearchParams = {},
        keyword = null,
        navParam = null,
        pageSize = 12,
    } = variables || {};
    const options = {
        variables : searcModelVariables,
        ssr: true,
        skip: !variables?.navParam && !variables?.keyword
    };
    const { data = {}, loading, error } = useDataModel('searchModel', options) || {};
    const { metadata } = data?.searchModel || {};
    const { contentType } = metadata || {};
    const isCategory = contentType === 'categorypage';

    if (isCategory) {
        return null;
    }

    if (!loading && store?.storeId !== '8119' && variables?.storeId === '8119') {
        localizedAndClientLoaded.current = true;
        loadedStore.current = {
            storeId: store?.storeId,
        };
    } else if (data && !loading && store?.isLocalized) {
        localizedAndClientLoaded.current = true;
        loadedStore.current = {
            storeId: store?.storeId,
        };
    }
    const newDefaultVariables = {
        ...defaultVariables.current,
        storeId: loadedStore.current?.storeId,
        skipKPF: true,
        skipSubscribeAndSave: true,
        skipInstallServices: false,
        isBrandPricingPolicyCompliant: localizedAndClientLoaded.current ? isCustomerIdentified : false
    };
    // Track previous values for change detection
    const previousBrandPricingCompliant = useRef(defaultVariables.isBrandPricingPolicyCompliant);
    const previousLoading = useRef(loading);

    // Reset samples flag on pagination
    useEffect(() => {
        samplesLoadedForCurrentPage.current = false;
    }, [variables?.startIndex]);

    // Reset when brand policy compliance changes
    useEffect(() => {
        if (
            previousBrandPricingCompliant.current === false &&
            defaultVariables.isBrandPricingPolicyCompliant === true
        ) {
            setClientSearchModelDone(false);
        }
        previousBrandPricingCompliant.current = defaultVariables.isBrandPricingPolicyCompliant;
    }, [defaultVariables.isBrandPricingPolicyCompliant]);

    // Mark done when loading transitions from true to false after login/brand policy change
    useEffect(() => {
        if (defaultVariables.isBrandPricingPolicyCompliant) {
            if (
                (previousLoading.current === true && loading === false) ||
                (previousLoading.current === false && loading === false && !clientSearchModelDone)
            ) {
                setClientSearchModelDone(true);
            }
        } else {
            setClientSearchModelDone(false);
        }
        previousLoading.current = loading;
    }, [loading, defaultVariables.isBrandPricingPolicyCompliant, clientSearchModelDone]);

    const getUniqueSkus = (products = []) => {
        const uniqueSkus = [...new Set(products.map((product) => product.itemId))];
        return uniqueSkus;
    };
    const hasCategoryCard = data?.searchModel?.orangeGraph?.universalSearchArray?.length > 0;
    const getBreakpoint = (width) => {
        if (width >= 1280) { return 1280; }
        if (width >= 1024) { return 1024; }
        return null;
    };
    const LAYOUT_CONFIG = {
        1280: { withCategory: 24, withoutCategory: 12 },
        1024: { withCategory: 18, withoutCategory: 9 }
    };
    const useInGridBannerResize = (isClientSearchModelResolved, hasCategoryCard, uniqueSkus) => {
        const [isInGridBannerBottom, setIsInGridBannerBottom] = useState(false);

        useEffect(() => {
            const onResize = debounce(200, () => {
                if (!isClientSearchModelResolved || typeof window === 'undefined') {
                    return;
                }

                const breakpoint = getBreakpoint(window.innerWidth);
                if (!breakpoint) {
                    return;
                }

                const config = LAYOUT_CONFIG[breakpoint];
                const threshold = hasCategoryCard ? config.withCategory : config.withoutCategory;
                const skuCount = uniqueSkus?.length || 0;

                setIsInGridBannerBottom(skuCount < threshold);
            });

            if (typeof window !== 'undefined') {
                window.addEventListener('resize', onResize);
                onResize();
            }

            return () => {
                if (typeof window !== 'undefined') {
                    window.removeEventListener('resize', onResize);
                }
            };
        }, [isClientSearchModelResolved, hasCategoryCard, uniqueSkus?.length]);

        return isInGridBannerBottom;
    };
    const products = data?.searchModel?.products || [];
    const uniqueSkus = getUniqueSkus(products);
    const isInGridBannerBottom = useInGridBannerResize(
        isClientSearchModelResolved,
        hasCategoryCard,
        uniqueSkus
    );
    const productsFinishedLoading = channel === 'desktop'
        ? (
            !samplesLoadedForCurrentPage.current &&
            !loading &&
            products?.length > 0 &&
            localizedAndClientLoaded.current &&
            (
                !defaultVariables.isBrandPricingPolicyCompliant
                || clientSearchModelDone
            )
        )
        : false;

    const { loading: plpSamplesLoading, data: plpSamplesData = {} } = usePLPSamples({
        products: channel === 'desktop' ? products : [], // Empty array for mobile
        productsFinishedLoading,
        storeId: store?.storeId,
        storeZip: store?.storeZip
    });

    const hasSampleData = plpSamplesData &&
        Object.keys(plpSamplesData).length > 0 &&
        plpSamplesData.searchModel?.products?.length > 0;

    // Mark samples as loaded after fetch completes
    useEffect(() => {
        if (productsFinishedLoading && !plpSamplesLoading && hasSampleData) {
            samplesLoadedForCurrentPage.current = true;
        }
    }, [productsFinishedLoading, plpSamplesLoading, hasSampleData]);

    const samplesLoading = (plpSamplesLoading || !hasSampleData || loading);

    const defaultGridProps = {
        itemId,
        productSSR,
        searchSSR,
        additionalSearchParams,
        keyword,
        nValue: navParam,
        storeId: loadedStore.current.storeId,
        pageType,
        handleProductClick,
        ref: resultsDimensionsRef,
        defaultVariables: newDefaultVariables,
        impProductPodData: impProductPodData(hybridLayoutID),
        searchModelData: data,
        searchModelLoading: loading,
        searchModelError: error,
        pageSize,
        ...(channel === 'desktop'
            ? {
                productPodHoverDelay: 500,
                isInGridBannerBottom,
                samplesLoading,
            }
            : {
                hasSameDayFilter: hasSameDayFilter(data?.searchModel?.taxonomy?.breadCrumbs),
                samplesLoading: true,
            }
        )
    };

    // useEffect for product change analytics
    useEffect(() => {
        if (data && !loading && isClientSearchModelResolved) {
            const searchNavPropsRef = {
                current: {
                    pageSize: variables?.pageSize,
                    askForSponsored: isSearch,
                    deliveryZip
                }
            };
            const analyticsSearchModel = {
                ...data?.searchModel || { searchReport: { ...searchNavPropsRef.current, startIndex: variables?.startIndex } },
                defaultPageSize: variables?.pageSize,
                pageID: isNIncluded(navParam) ? navParam : 'N-' + navParam,
                isHybridContentful: isHybridLayout,
                isBrandPage
            };
            if (pageType === 'browse') {
                LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('browse.new-products', analyticsSearchModel);
            } else if (pageType === 'search') {
                LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search.new-products', analyticsSearchModel);
            }
        }
    }, [stringifyItemIds(data), stringifyAppliedRefinements(data), isClientSearchModelResolved]);

      // useEffect for pep analytics
    useEffect(() => {
        if (data && !loading && isClientSearchModelResolved && pageType === 'pep') {
            LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('pep.new-products', {
                ...data?.searchModel,
                defaultPageSize: variables?.pageSize,
            });
        }
    }, [stringifyItemIds(data), isClientSearchModelResolved, pageType]);

    return (
        <ImpressionProvider data={impProductPodData(hybridLayoutID)}>
            <DefaultGrid {...defaultGridProps} />
        </ImpressionProvider>
    );
});

DefaultGridComposable.displayName = 'DefaultGridComposable';
DefaultGridComposable.dataModel = extend({
    layouts: params({
        slug: string().isRequired(),
        customerType: string(),
        isHybrid: bool()
    }).shape({
        id: string(),
        slug: string(),
        type: string(),
        title: string(),
    })
}, DefaultGrid,
    OrderSample);

DefaultGridComposable.propTypes = {
    itemId: PropTypes.string,
    productSSR: PropTypes.bool,
    searchSSR: PropTypes.bool
};

DefaultGridComposable.defaultProps = {
    itemId: null,
    productSSR: null,
    searchSSR: true
};

export { DefaultGridComposable };